import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { t, Trans } from '@lingui/macro';
import { Button, Tab, Tabs } from '@material-ui/core';
import { get, isEmpty } from 'lodash';

import AdminPage from '../AdminPage';
import { AdminPages, buildCreatePageTarget, buildPageId, buildRootPageTarget } from '../AdminLinkUtils';
import Actions from '../../page-framework/actions/Actions';
import Link from '../../links/Link';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useDataService } from '../../../services/ApiDataService';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';
import useToasts, { createToastConfig, SUCCESS as TOAST_SUCCESS } from '../../../stores/ToastStore';
import { TEST_TYPES_TAB, TESTS_TAB } from './TestTypesAdminConstants';
import { deleteManyTests, updateTestSelection } from './TestTypesApi';
import TestTypesListPanel from './TestTypesListPanel';
import TestTypeImportDialog from './TestTypeImportDialog';
import TestErrorListPanel from './TestErrorListPanel';

const PAGE_STATE_PARAM = 'pageState';
const TESTS_ERRORS_PAGE_STATE = 'tests-errors-list';
const TESTS_LIST_PAGE_STATE = 'tests-list';

function getTabFromLocation(search) {
    const params = new URLSearchParams(search || '');
    const pageState = params.get(PAGE_STATE_PARAM);
    return pageState === TESTS_ERRORS_PAGE_STATE || pageState === TESTS_LIST_PAGE_STATE ? TESTS_TAB : TEST_TYPES_TAB;
}

export default function TestTypesAdminPage() {
    const { search } = useLocation();
    const [activeTab, setActiveTab] = useState(() => getTabFromLocation(search));
    const [importOpen, setImportOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [errorListSelection, setErrorListSelection] = useState(null);
    const [errorListTableState, setErrorListTableState] = useState(null);
    const dataService = useDataService();
    const [, uiAlertActions] = useUiAlerts();
    const [, toastActions] = useToasts();

    const handleRefresh = useCallback(() => {
        setRefreshKey((k) => k + 1);
    }, []);

    const hasSelection = errorListSelection && (!isEmpty(errorListSelection.selection) || errorListSelection.inverted);
    const canCopy = errorListSelection && !errorListSelection.inverted && errorListSelection.selection?.length === 1;
    const tableRowsRef = useRef([]);

    const handleErrorListSelectionChange = useCallback((selectionModel, tableRows) => {
        setErrorListSelection(selectionModel);
        if (tableRows) {
            tableRowsRef.current = tableRows;
        }
    }, []);

    const handleErrorListTableStateChange = useCallback((tableState) => {
        setErrorListTableState(tableState);
    }, []);

    const buildRequestParams = useCallback(() => {
        return {
            search: errorListTableState?.search || '',
            searchFields: ['name', 'status', 'tags', 'businessServicesSortable'],
            metrics: ['statusCounts'],
        };
    }, [errorListTableState]);

    const handleBulkAction = useCallback(
        async (actionFn, successMessage, errorMessage) => {
            try {
                await actionFn();
                toastActions.createToast(createToastConfig(TOAST_SUCCESS, successMessage));
                setRefreshKey((k) => k + 1);
            } catch {
                uiAlertActions.createAlert(createAlertConfig(ERROR, errorMessage));
            }
        },
        [uiAlertActions, toastActions]
    );

    const handleEnable = useCallback(() => {
        const requestParams = buildRequestParams();
        return handleBulkAction(
            () => updateTestSelection(dataService, requestParams, errorListSelection, { status: 'Running' }),
            t`Successfully enabled selected Tests`,
            t`Problem enabling selected Tests`
        );
    }, [dataService, errorListSelection, buildRequestParams, handleBulkAction]);

    const handleDisable = useCallback(() => {
        const requestParams = buildRequestParams();
        return handleBulkAction(
            () => updateTestSelection(dataService, requestParams, errorListSelection, { status: 'Disabled' }),
            t`Successfully disabled selected Tests`,
            t`Problem disabling selected Tests`
        );
    }, [dataService, errorListSelection, buildRequestParams, handleBulkAction]);

    const handleDelete = useCallback(() => {
        const requestParams = buildRequestParams();
        return handleBulkAction(
            () => deleteManyTests(dataService, requestParams, errorListSelection),
            t`Tests deleted successfully`,
            t`Failed to delete tests`
        );
    }, [dataService, errorListSelection, buildRequestParams, handleBulkAction]);

    const copyTarget = useMemo(() => {
        if (!canCopy) return null;
        const selectedId = errorListSelection.selection[0];
        const rows = Object.values(tableRowsRef.current || {});
        const row = rows.find((r) => get(r, 'row.test._id') === selectedId);
        const group = encodeURIComponent(get(row, 'row.test.testType.group', ''));
        return `${buildRootPageTarget(AdminPages.ServiceTests)}/${group}/tests/new?dupOf=${encodeURIComponent(
            selectedId
        )}`;
    }, [canCopy, errorListSelection]);

    const onTabChange = (_, nextTab) => {
        if (!nextTab) {
            return;
        }

        setTabWithPageState(nextTab);
    };

    useEffect(() => {
        setActiveTab(getTabFromLocation(search));
    }, [search]);

    const setTabWithPageState = useMemo(() => {
        return (nextTab) => {
            setActiveTab(nextTab);

            const params = new URLSearchParams(window.location.search || '');
            if (nextTab === TESTS_TAB) {
                params.set(PAGE_STATE_PARAM, TESTS_ERRORS_PAGE_STATE);
            } else {
                params.delete(PAGE_STATE_PARAM);
            }

            const query = params.toString();
            const basePath = buildRootPageTarget(AdminPages.ServiceTests);
            const nextUrl = query ? `${basePath}?${query}` : basePath;
            window.history.replaceState(null, '', nextUrl);
        };
    }, []);

    return (
        <AdminPage
            title={t`Service Tests`}
            pageId={buildPageId(AdminPages.ServiceTests)}
            actions={
                activeTab === TEST_TYPES_TAB ? (
                    <Actions>
                        <Button
                            component={Link}
                            to={buildCreatePageTarget(AdminPages.ServiceTests)}
                            variant="contained"
                            color="primary"
                        >
                            <Trans>Create</Trans>
                        </Button>
                        <Button variant="contained" color="primary" onClick={() => setImportOpen(true)}>
                            <Trans>Import</Trans>
                        </Button>
                    </Actions>
                ) : (
                    <Actions>
                        <ConfirmDeleteButton
                            disabled={!hasSelection}
                            handleConfirmedDelete={handleDelete}
                            plural={errorListSelection?.selection?.length > 1}
                        />
                        <Button onClick={handleEnable} disabled={!hasSelection}>
                            <Trans>Enable</Trans>
                        </Button>
                        <Button onClick={handleDisable} disabled={!hasSelection}>
                            <Trans>Disable</Trans>
                        </Button>
                        <Button component={Link} to={copyTarget || '#'} disabled={!canCopy}>
                            <Trans>Copy</Trans>
                        </Button>
                    </Actions>
                )
            }
        >
            <>
                <Tabs value={activeTab} onChange={onTabChange} aria-label="service tests tabs">
                    <Tab
                        id="service-tests-tab-types"
                        value={TEST_TYPES_TAB}
                        label={<Trans>Service Test Types</Trans>}
                    />
                    <Tab id="service-tests-tab-tests" value={TESTS_TAB} label={<Trans>Service Tests</Trans>} />
                </Tabs>

                {activeTab === TEST_TYPES_TAB && <TestTypesListPanel key={refreshKey} />}
                {activeTab === TESTS_TAB && (
                    <TestErrorListPanel
                        key={refreshKey}
                        onSelectionChange={handleErrorListSelectionChange}
                        onTableStateChange={handleErrorListTableStateChange}
                    />
                )}
                <TestTypeImportDialog
                    open={importOpen}
                    onClose={() => setImportOpen(false)}
                    onRefresh={handleRefresh}
                />
            </>
        </AdminPage>
    );
}
